import { useCallback, useEffect, useMemo, useState } from "react";
import { useUrlState } from "@/lib/navState";
import { api, formatApiErrorDetail } from "@/lib/api";
import { label } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, Search, PackageCheck, Truck } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["PUTTY", "SANDPAPER", "MASKING", "SEALER", "ADHESIVE", "COMPOUND", "THINNER", "WELDING", "OTHER"];

function AddItem({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", category: "OTHER", unit: "pcs", min_stock: 0 });
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Enter material name"); return; }
    try { await api.post("/materials", { ...f, min_stock: Number(f.min_stock) || 0 }); toast.success("Material added"); setOpen(false); setF({ name: "", category: "OTHER", unit: "pcs", min_stock: 0 }); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="material-add-btn" size="sm" className="bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]"><Plus size={14} className="mr-1" /> Add Item</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Add Material</DialogTitle><DialogDescription>Create a consumable stock item.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs">Name *</Label><Input data-testid="material-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="text-xs">Category</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="material-category" className="mt-1.5 h-9"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{label(c)}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Unit</Label><Input data-testid="material-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs">Min Stock</Label><Input data-testid="material-min" type="number" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="flex justify-end"><Button data-testid="material-add-confirm" onClick={submit} className="bg-[#111827] text-white hover:bg-[#1F2937]">Add</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StockTxn({ item, kind, onDone }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [plate, setPlate] = useState("");
  const [notes, setNotes] = useState("");
  const isIssue = kind === "issue";
  const submit = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Enter quantity"); return; }
    if (isIssue && n > item.qty_on_hand) { toast.error(`Only ${item.qty_on_hand} in stock`); return; }
    try {
      await api.post(`/materials/${item.id}/${kind}`, { qty: n, license_plate: plate, notes });
      toast.success(isIssue ? "Issued to repair order" : "Stock received");
      setOpen(false); setQty(""); setPlate(""); setNotes(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`material-${kind}-open-${item.id}`} size="sm" variant={isIssue ? "default" : "outline"} className={isIssue ? "bg-[#111827] text-white hover:bg-[#1F2937]" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}>
          {isIssue ? <><Truck size={14} className="mr-1" /> Issue</> : <><PackageCheck size={14} className="mr-1" /> Receive</>}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">{isIssue ? "Issue to Repair Order" : "Receive Stock"}</DialogTitle><DialogDescription>{item.name} · on hand {item.qty_on_hand} {item.unit}</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs">Quantity *</Label><Input data-testid={`material-${kind}-qty-${item.id}`} type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" /></div>
          {isIssue && <div><Label className="text-xs">License Plate / Repair Order (optional)</Label><Input data-testid={`material-issue-plate-${item.id}`} value={plate} onChange={(e) => setPlate(e.target.value)} className="mt-1.5" placeholder="e.g. H 1988 BAG" /></div>}
          <div><Label className="text-xs">Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" /></div>
          <div className="flex justify-end"><Button data-testid={`material-${kind}-confirm-${item.id}`} onClick={submit} className={isIssue ? "bg-[#111827] text-white" : "bg-emerald-600 text-white"}>Confirm</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WarehouseMaterial() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("ALL");
  const [q, setQ] = useUrlState("q", "");
  const load = useCallback(async () => { try { const { data } = await api.get("/materials"); setItems(data || []); } catch (e) { /* ignore */ } }, []);
  useEffect(() => { load(); }, [load]);
  const visible = useMemo(() => items.filter((m) => (cat === "ALL" || m.category === cat) && (!q || m.name.toLowerCase().includes(q.toLowerCase()))), [items, cat, q]);

  return (
    <div className="ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-6">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#111827] sm:text-2xl"><Package size={26} className="mr-2 inline text-[#3B82F6]" />Material Warehouse</h1>
          <p className="mt-1 text-sm text-gray-500">Consumable stock · Receive → Stock → Issue to Repair Order</p>
        </div>
        <AddItem onDone={load} />
      </div>
      <div className="relative mt-3">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input data-testid="material-search" placeholder="Search material…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-10" />
      </div>
      <div className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {["ALL", ...CATEGORIES].map((c) => (
          <button key={c} data-testid={`material-cat-${c}`} onClick={() => setCat(c)} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${cat === c ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>{label(c)}</button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visible.map((m) => {
          const low = Number(m.qty_on_hand) <= Number(m.min_stock || 0);
          return (
            <div key={m.id} data-testid={`material-${m.id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{m.name}</span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">{label(m.category)}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                <span className={`rounded-full px-2 py-0.5 font-semibold ${low ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>On hand {m.qty_on_hand} {m.unit}</span>
                {low && <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">LOW</span>}
                <span className="text-gray-400">Recv {m.qty_received} · Used {m.qty_used}</span>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <StockTxn item={m} kind="receive" onDone={load} />
                <StockTxn item={m} kind="issue" onDone={load} />
              </div>
            </div>
          );
        })}
        {visible.length === 0 && <p className="py-10 text-center text-sm text-gray-400 sm:col-span-2">No materials yet. Use "Add Item" to create stock.</p>}
      </div>
    </div>
  );
}
